import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system';
import React from 'react';

import { HIGH_PRECISION_DECIMAL_PLACES } from '@/constants';
import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { useTranslator } from '@/hooks/useTranslator';
import { CurrencyCodeType } from '@/types/currency';
import { FxLockType, SimulationResponse } from '@/types/simulation';

const FX_LOCK_DURATION_HOURS: Record<FxLockType, number> = {
  [FxLockType.DEFAULT]: 2,
  [FxLockType.FX_LOCK_5]: 5,
  [FxLockType.FX_LOCK_24]: 24,
  [FxLockType.FX_LOCK_48]: 48,
  [FxLockType.FX_LOCK_72]: 72,
};

const FX_LOCK_ORDER: readonly FxLockType[] = [
  FxLockType.DEFAULT,
  FxLockType.FX_LOCK_5,
  FxLockType.FX_LOCK_24,
  FxLockType.FX_LOCK_48,
  FxLockType.FX_LOCK_72,
];

const MAJOR_CURRENCIES = [CurrencyCodeType.USD, CurrencyCodeType.USDC, CurrencyCodeType.USDT];

const SPREAD_FORMATTER_CONFIG = {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
};

const RATE_FORMATTER_CONFIG = {
  maximumFractionDigits: HIGH_PRECISION_DECIMAL_PLACES,
  minimumFractionDigits: HIGH_PRECISION_DECIMAL_PLACES,
};

const formatRateDisplay = (
  rate: number | undefined | null,
  fromCurrency: CurrencyCodeType,
  toCurrency: CurrencyCodeType,
  rateFormatter: Intl.NumberFormat,
): string => {
  if (!rate || rate === 0 || Number.isNaN(rate)) {
    return `1 ${fromCurrency} = 1 ${toCurrency}`;
  }

  if (MAJOR_CURRENCIES.includes(toCurrency)) {
    const invertedRate = 1 / rate;
    return `1 ${toCurrency} = ${rateFormatter.format(invertedRate)} ${fromCurrency}`;
  }

  return `1 ${fromCurrency} = ${rateFormatter.format(rate)} ${toCurrency}`;
};

interface Props {
  readonly options: readonly SimulationResponse[];
  readonly selectedFxLockType: FxLockType;
  readonly onSelect: (type: FxLockType) => void;
  readonly isLoading: boolean;
  readonly disabled?: boolean;
  readonly fromCurrency: CurrencyCodeType;
  readonly toCurrency: CurrencyCodeType;
}

export const FxRateLockSelector: React.FC<Props> = (props: Props): React.ReactElement | null => {
  const { options, selectedFxLockType, onSelect, isLoading, disabled, fromCurrency, toCurrency } =
    props;
  const tr = useTranslator();
  const spreadFormatter = useLocalizedFormatter(SPREAD_FORMATTER_CONFIG);
  const rateFormatter = useLocalizedFormatter(RATE_FORMATTER_CONFIG);

  const sortedOptions = React.useMemo((): readonly SimulationResponse[] => {
    return [...options].sort(
      (a: SimulationResponse, b: SimulationResponse): number =>
        FX_LOCK_ORDER.indexOf(a.fxLockType ?? FxLockType.DEFAULT) -
        FX_LOCK_ORDER.indexOf(b.fxLockType ?? FxLockType.DEFAULT),
    );
  }, [options]);

  const handleChange = React.useCallback(
    (event: SelectChangeEvent<FxLockType>): void => {
      onSelect(event.target.value as FxLockType);
    },
    [onSelect],
  );

  const getOptionLabel = React.useCallback(
    (option: SimulationResponse): string => {
      const fxLockType = option.fxLockType ?? FxLockType.DEFAULT;
      const isDefault = fxLockType === FxLockType.DEFAULT;
      const hours = FX_LOCK_DURATION_HOURS[fxLockType];

      const duration = isDefault ? tr('FX_LOCK_DEFAULT_LABEL') : tr('FX_LOCK_HOURS', String(hours));
      const spread =
        isDefault || !option.fxLockSpreadPercentage
          ? tr('FX_LOCK_NO_EXTRA_SPREAD')
          : tr('FX_LOCK_SPREAD', `+${spreadFormatter.format(option.fxLockSpreadPercentage)}`);

      return tr('FX_LOCK_OPTION', duration, spread);
    },
    [tr, spreadFormatter],
  );

  const getOptionRate = React.useCallback(
    (option: SimulationResponse): string => {
      const rate = option.transactionDetails.conversionDetails?.clientRate;
      return formatRateDisplay(rate, fromCurrency, toCurrency, rateFormatter);
    },
    [fromCurrency, toCurrency, rateFormatter],
  );

  if (isLoading) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (options.length === 0) {
    return null;
  }

  return (
    <FormControl fullWidth>
      <InputLabel>{tr('FX_RATE_LOCK')}</InputLabel>
      <Select<FxLockType>
        label={tr('FX_RATE_LOCK')}
        value={selectedFxLockType}
        onChange={handleChange}
        disabled={disabled}
        renderValue={(value: FxLockType): string => {
          const option = sortedOptions.find(
            (o: SimulationResponse): boolean => (o.fxLockType ?? FxLockType.DEFAULT) === value,
          );
          return option ? getOptionLabel(option) : '';
        }}>
        {sortedOptions.map((option: SimulationResponse): React.ReactElement => {
          const fxLockType = option.fxLockType ?? FxLockType.DEFAULT;

          return (
            <MenuItem key={fxLockType} value={fxLockType}>
              <Box sx={styles.menuItemContent}>
                <Typography variant="body2">{getOptionLabel(option)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {getOptionRate(option)}
                </Typography>
              </Box>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

const styles: Record<string, SystemStyleObject> = {
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    py: 2,
  },
  menuItemContent: {
    display: 'flex',
    flexDirection: 'column',
  },
};
